// File: src/utils/roles.js
// The roles an account can hold, and how to say them out loud.
//
// This list must match USER_ROLES in backend/middleware.js and the enum on
// users.role - offering a role the column cannot store produces a 400 nobody
// can act on. It lived twice, copied between the admin panel and the user
// screen, and had already drifted once.

import { useCallback } from 'react';
import { useLanguage } from '../contexts/LanguageContext';

// Ordered by reach, which is also the order they read best in a dropdown.
export const USER_ROLES = ['super_admin', 'country_admin', 'editor', 'organizer', 'competitor'];

// The roles that administer championship data: series, races, drivers and
// results. Two of the three see one country only, but that is the server's
// business - here it just decides which screens are worth rendering.
export const STAFF_ROLES = ['super_admin', 'country_admin', 'editor'];

// 'admin' was the old name for unrestricted access. A session that started
// before the rename still has it in localStorage, and the server still answers
// to it, so the UI has to as well.
export const normalizeRole = (role) => (role === 'admin' ? 'super_admin' : role);

// Roles this person may hand out. Only a super admin can make another one -
// the server enforces this too, and refusing it here just avoids offering a
// choice that would come back 403.
export const assignableRoles = (permissions) =>
  permissions.isSuperAdmin() ? USER_ROLES : USER_ROLES.filter(role => role !== 'super_admin');

const LABEL_KEYS = {
  super_admin: 'superAdmin',
  country_admin: 'countryAdmin',
  editor: 'editor',
  organizer: 'organizer',
  competitor: 'competitor'
};

// Tailwind cannot see class names built at runtime, so each is written in full.
export const ROLE_BADGE_CLASSES = {
  super_admin: 'bg-yellow-100 dark:bg-yellow-500/20 text-yellow-700 dark:text-yellow-400',
  country_admin: 'bg-orange-100 dark:bg-orange-500/20 text-orange-700 dark:text-orange-400',
  editor: 'bg-blue-100 dark:bg-blue-500/20 text-blue-700 dark:text-blue-400',
  organizer: 'bg-green-100 dark:bg-green-500/20 text-green-700 dark:text-green-400',
  competitor: 'bg-paper-alt text-ink-muted'
};

export const roleBadgeClass = (role) =>
  ROLE_BADGE_CLASSES[normalizeRole(role)] || ROLE_BADGE_CLASSES.competitor;

/** The name of a role in the language on screen. */
export function useRoleLabel() {
  const { t } = useLanguage();
  return useCallback(
    (role) => (LABEL_KEYS[normalizeRole(role)] ? t(LABEL_KEYS[normalizeRole(role)]) : role),
    [t]
  );
}
